<section class="step-editor">
  <h2 class="step-editor__title">{{ 'pipeline_step_editor.title' | translate }}</h2>

  <div class="step-editor__section">
    <label class="step-editor__label">{{ 'pipeline_step_editor.task.label' | translate }}</label>
    <div class="step-editor__tasks">
      <!-- The tooltip lives on a wrapper span because matTooltip does not fire on
           disabled buttons (the browser suppresses their pointer events). -->
      <span
        *ngFor="let item of facade.availableTasks$ | async; trackBy: trackByTask"
        class="step-editor__task-option-wrapper"
        [matTooltip]="item.reasonKey ? (item.reasonKey | translate) : ''"
        [matTooltipDisabled]="!(item.disabled && item.reasonKey)"
        [matTooltipShowDelay]="350"
      >
        <button
          type="button"
          class="step-editor__task-option"
          [class.step-editor__task-option--selected]="(facade.task$ | async) === item.task"
          [disabled]="item.disabled"
          (click)="onTaskClicked(item.task)"
        >
          {{ 'pipeline_step_editor.task.options.' + item.task | translate }}
        </button>
      </span>
    </div>
  </div>

  <ng-container *ngIf="(facade.task$ | async) !== null">
    <div class="step-editor__executor" *ngIf="showAlgorithmSection$ | async">
      <label class="step-editor__section-title">{{ 'pipeline_step_editor.executor.title' | translate }}</label>
      <p class="step-editor__section-description">
        {{ 'pipeline_step_editor.executor.description' | translate }}
      </p>
      <ts-algorithm-switcher></ts-algorithm-switcher>
    </div>

    <ts-cloud-subject-selector
      *ngIf="showCloudSubject$ | async"
      [value]="(facade.cloudSubject$ | async) ?? null"
      (valueChange)="onCloudSubjectChanged($event)"
    ></ts-cloud-subject-selector>

    <ts-generate-finding-selector
      *ngIf="showGenerateFinding$ | async"
      [value]="(facade.generateFinding$ | async) ?? null"
      (valueChange)="onGenerateFindingChanged($event)"
    ></ts-generate-finding-selector>

    <ng-container *ngIf="facade.showStructuredSections$ | async">
      <ts-category-management-list
        *ngIf="showCategoriesSection$ | async"
        [items]="(categoryItems$ | async) ?? []"
        [loading]="false"
        [canEdit]="(facade.isAdmin$ | async) ?? false"
        [canAdd]="(facade.canAddCategoryAllowed$ | async) ?? false"
        (addClicked)="onAddCategory()"
        (editClicked)="onEditCategory($event)"
        (removeConfirmed)="onRemoveCategory($event)"
        (reordered)="onCategoriesReordered($event)"
      ></ts-category-management-list>

      <div class="step-editor__checkboxes">
        <mat-checkbox
          *ngIf="showMultipleCheckbox$ | async"
          [checked]="(facade.multiple$ | async) ?? false"
          (change)="onMultipleChanged($event.checked)"
        >
          {{ 'pipeline_step_editor.checkbox.multiple' | translate }}
        </mat-checkbox>
        <mat-checkbox
          *ngIf="showAllowNewOptionsCheckbox$ | async"
          [checked]="(facade.allowNewOptions$ | async) ?? false"
          (change)="onAllowNewOptionsChanged($event.checked)"
        >
          {{ 'pipeline_step_editor.checkbox.allow_new_options' | translate }}
        </mat-checkbox>
        <mat-checkbox
          *ngIf="showMosaicViewCheckbox$ | async"
          [checked]="(facade.enableMosaicView$ | async) ?? false"
          (change)="onMosaicViewChanged($event.checked)"
        >
          {{ 'pipeline_step_editor.checkbox.enable_mosaic_view' | translate }}
        </mat-checkbox>
        <mat-checkbox
          *ngIf="showCounterCheckbox$ | async"
          [checked]="(facade.counter$ | async) ?? false"
          (change)="onCounterChanged($event.checked)"
        >
          {{ 'pipeline_step_editor.checkbox.counter' | translate }}
        </mat-checkbox>
      </div>

      <div *ngIf="showMappingPlaceholder$ | async"
        class="step-editor__mapping-placeholder">
        {{ 'pipeline_step_editor.mapping_widget_coming_soon' | translate }}
      </div>
    </ng-container>

    <section
      *ngIf="facade.manualMode$ | async"
      class="step-editor__manual"
      data-cy="step-editor-manual-panel"
    >
      <p class="step-editor__manual-warning">
        {{ 'pipeline_step_editor.manual_mode.warning' | translate }}
      </p>
      <ts-json-editor
        [value]="facade.manualParams$ | async"
        [showHeaderActions]="false"
        (parseStateChange)="onManualParamsChange($event)"
      ></ts-json-editor>
    </section>
  </ng-container>

  <!-- Standing, derived from the categories the form currently holds, so it
       appears on open and clears itself once the labels are named. Rendered
       above the error banner: this one describes the step, the one below
       complains about the last action. -->
  <div
    *ngIf="facade.undefinedLabelsWarning$ | async as warningKey"
    class="step-editor__label-warning"
    data-cy="step-editor-undefined-labels-warning"
  >
    <i class="ri-error-warning-line step-editor__label-warning-icon" aria-hidden="true"></i>
    <span>{{ warningKey | translate }}</span>
  </div>

  <div *ngIf="facade.error$ | async as error" class="step-editor__error">{{ error | translate }}</div>

  <div *ngIf="facade.loading$ | async" class="step-editor__loading">
    <mat-spinner diameter="32"></mat-spinner>
  </div>

  <div class="step-editor__actions">
    <button
      type="button"
      class="btn btn-outline-primary step-editor__action"
      data-cy="step-editor-cancel"
      (click)="onCancel()"
    >
      <i class="ri-close-fill"></i>
      {{ 'pipeline_step_editor.button.cancel' | translate }}
    </button>
    <!-- Tooltip on the wrapper span: matTooltip does not fire on the disabled button. -->
    <span
      *ngIf="facade.canEnterManualMode$ | async"
      class="step-editor__action-tooltip"
      [matTooltip]="'pipeline_step_editor.manual_mode.select_task_first' | translate"
      [matTooltipDisabled]="(facade.task$ | async) !== null"
      [matTooltipShowDelay]="350"
    >
      <button
        type="button"
        class="btn step-editor__action step-editor__action--manual"
        data-cy="step-editor-manual"
        [disabled]="(facade.task$ | async) === null"
        (click)="onManualMode()"
      >
        <i class="ri-error-warning-line"></i>
        {{ 'pipeline_step_editor.button.manual_mode' | translate }}
      </button>
    </span>
    <!-- Tooltip on the wrapper span: matTooltip does not fire on the disabled button. -->
    <span
      *ngIf="facade.manualMode$ | async"
      class="step-editor__action-tooltip"
      [matTooltip]="'pipeline_step_editor.manual_mode.exit_disabled' | translate"
      [matTooltipDisabled]="(facade.manualParamsStructuredCompatible$ | async) ?? false"
      [matTooltipShowDelay]="350"
    >
      <button
        type="button"
        class="btn step-editor__action step-editor__action--manual"
        data-cy="step-editor-exit-manual"
        [disabled]="!(facade.manualParamsStructuredCompatible$ | async)"
        (click)="onExitManualMode()"
      >
        <i class="ri-arrow-go-back-line"></i>
        {{ 'pipeline_step_editor.button.exit_manual_mode' | translate }}
      </button>
    </span>
    <button
      type="button"
      class="btn btn-primary step-editor__action"
      data-cy="step-editor-save"
      [disabled]="!(facade.canSubmit$ | async)"
      (click)="onSave()"
    >
      <i class="ri-save-line"></i>
      {{ 'button.continue' | translate }}
    </button>
  </div>
</section>
